import axios, { AxiosInstance, AxiosResponse } from "axios";
import { setAuthState } from "../store/slices/authSlice";
import { imsIdsConfig } from "./idsConfig";
import { ENV_VARIABLES } from "../Config/env-config";

// Define your base URL and any default headers you want to include
const baseURL = ENV_VARIABLES.REACT_APP_API_BASE_URL;
const defaultHeaders = {
  "Content-Type": "application/json",
};

//Custom Axios instance with default configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: baseURL,
  headers: defaultHeaders,
});

axiosInstance.interceptors.request.use(
  (config) => {
    // Get the access token from localStorage
    const accessToken = localStorage.getItem("access_token");

    // If access token exists, add it to the Authorization header
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    // Handle request errors
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle response errors
    const originalRequest = error.config;

    // If the error is due to unauthorized (401), attempt to refresh the token
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true; // Prevent infinite loop

      try {
        const refreshedResponse: AxiosResponse = await refreshToken(); // Call refreshToken function

        // Update the Authorization header with the new access token
        if (refreshedResponse) {
          originalRequest.headers.Authorization = `Bearer ${refreshedResponse.data.access_token}`;

          // Save access token to local storage
          localStorage.setItem("access_token", refreshedResponse.data.access_token);
          localStorage.setItem("refresh_token", refreshedResponse.data.refresh_token);

          // Update state
          setAuthState({
            access_token: refreshedResponse.data.access_token,
            refresh_token: refreshedResponse.data.refresh_token,
          });
        }

        // Retry original request with updated headers
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Error refreshing token:", refreshError);
        return Promise.reject(refreshError);
      }
    }

    // If the error is not related to token expiration, re-throw it
    return Promise.reject(error);
  }
);

// Function to refresh the access token
const refreshToken = async (): Promise<AxiosResponse<any>> => {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    const idsIssuer = localStorage.getItem("idsIssuer");
    const clientId = imsIdsConfig.client_id;

    if (!refreshToken || !idsIssuer) {
      throw new Error("Required token or issuer information is missing");
    }

    const response: AxiosResponse = await axios.post(
      `${idsIssuer}/connect/token`,
      {
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: clientId,
      },
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    // Return AxiosResponse
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const api = axiosInstance;
